/**
 * LobbyShell: the game room lobby UI.
 *
 * Per IMPLEMENTATION_PLAN §13.8 (Design H1, M3.T6.5):
 *
 *   [1] Room name + share button (40% visual weight, top-left)
 *   [2] Player list with avatars + host badge (25% weight, center)
 *   [3] Start Game button (20% weight, bottom-center, large)
 *   [4] Settings panel (15% weight, right rail, collapsible)
 *   [5] Custom word list textarea (revealed only when "Custom" selected)
 *
 * User eye path: room-name confirmation → see who's here → Start (host) / wait.
 * Settings are secondary, not a checklist.
 *
 * M3 scope: lobby UI + settings. Game start dispatches START_GAME to server.
 * Player join flow: name input + color picker on first load.
 * Custom word validation is via validateCustomWords() from shared.
 */

import { useState, useEffect, useRef } from "preact/hooks";
import type { CustomWord, RoomSettings, SerializedPlayer } from "@drawlol/shared";
import { validateCustomWords, MAX_GRAPHEMES } from "@drawlol/shared";
import type { ClientControlFrame } from "../ws.js";
import { ScribbleButton } from "../components/ScribbleButton.js";
import { ScribbleSelect } from "../forms/ScribbleSelect.js";
import { ScribbleToggle } from "../forms/ScribbleToggle.js";
import { PaperBanner } from "../components/PaperBanner.js";
import { ShareLinkCTA } from "../components/ShareLinkCTA.js";
import { EmptyRoomNudge } from "../components/EmptyRoomNudge.js";
import { ScribbleUnderline } from "../marks/v1/ScribbleUnderline.js";
import { SCRIBBLE_IN_CLASS } from "../anim/scribble-in.js";
import { PAGE_TEAR_IN_CLASS } from "../anim/page-tear.js";
import { SoundToggle } from "../audio/SoundToggle.js";

// Minimum player count that unlocks the START_GAME button. Server has
// the same constant in `server/src/index.ts`; keep both in sync.
const MIN_PLAYERS_TO_START = 2;

/**
 * Q9 (audit 2026-06-11): how long "Starting…" may sit unanswered before the
 * button frees itself. Success normally routes away in well under a second
 * (PHASE_CHANGED); a reject lands as AUTH_REJECT. This is the backstop for
 * the silent third case (frame lost mid-flight), without it the host was
 * soft-locked on "Starting…" forever, observed live.
 */
export const START_LATCH_TIMEOUT_MS = 5000;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type LobbySettings = {
  rounds: number;
  drawTimerSeconds: number;
  wordPickTimerSeconds: number;
  hintsEnabled: boolean;
  useCustomWords: boolean;
  customWordsText: string;
};

type LobbyShellProps = {
  roomId: string;
  players: ReadonlyArray<SerializedPlayer>;
  playerId: string | null;
  hostId: string;
  /**
   * Send a JSON control frame on the room WS. Provided by RoomShell so the
   * Start button, settings panel, and kick buttons can actually dispatch
   * server-bound actions (M3.fix B1 + B3 + B9).
   *
   * Optional so the unit tests can render LobbyShell without a real socket.
   * Defaults to a no-op when omitted.
   */
  sendFrame?: (frame: ClientControlFrame) => boolean;
  /**
   * Q9 (audit 2026-06-11): last in-room AUTH_REJECT from the server,
   * threaded from `ConnState.lastAuthReject` by RoomShell. A
   * "not-authorized-start" reject resets the "Starting…" latch, the
   * server said no, so the button must come back.
   */
  authReject?: { reason: string; at: number };
  /**
   * Task #15: the room's canonical settings from the server (snapshot +
   * SETTINGS_UPDATED frames, via ConnState). Guests render these directly
   *: their panel used to show DEFAULT_SETTINGS regardless of what the
   * host picked. Hosts seed their editable local state from them once
   * (covers a resumed host) and again on host promotion (migration).
   */
  serverSettings?: RoomSettings;
};

/**
 * Project the server's canonical RoomSettings into the client's editable
 * shape. customWords round-trips through the same text syntax the
 * validator parses: untagged lines are medium by definition, so medium
 * entries reconstruct untagged and easy/hard keep their ":tier" suffix.
 */
export function fromServerSettings(s: RoomSettings): LobbySettings {
  return {
    rounds: s.rounds,
    drawTimerSeconds: s.drawTimerSeconds,
    wordPickTimerSeconds: s.wordPickTimerSeconds,
    hintsEnabled: s.hintsEnabled,
    useCustomWords: s.useCustomWords,
    // Defensive Array.isArray (review P2): the server sanitizes
    // UPDATE_SETTINGS payloads, but a render-path .map must never be one
    // bad frame away from white-screening the lobby.
    customWordsText: (Array.isArray(s.customWords) ? s.customWords : [])
      .map((w) => (w.tier === "medium" ? w.word : `${w.word}:${w.tier}`))
      .join("\n"),
  };
}

const DEFAULT_SETTINGS: LobbySettings = {
  rounds: 2,
  drawTimerSeconds: 75,
  wordPickTimerSeconds: 15,
  hintsEnabled: true,
  useCustomWords: false,
  customWordsText: "",
};

const ROUNDS_OPTIONS = [1, 2, 3, 4, 5].map((n) => ({
  value: String(n),
  label: `${n} round${n === 1 ? "" : "s"}`,
}));

const DRAW_TIMER_OPTIONS = [30, 60, 75, 90, 120].map((s) => ({
  value: String(s),
  label: `${s}s`,
}));

const WORD_PICK_TIMER_OPTIONS = [10, 15, 20].map((s) => ({
  value: String(s),
  label: `${s}s`,
}));

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getHostName(players: ReadonlyArray<SerializedPlayer>, hostId: string): string {
  return players.find((p) => p.id === hostId)?.name ?? "Host";
}

/**
 * Machine reason codes from validate-custom-words → human one-liners (audit
 * 2026-06-11: hosts were shown the raw codes, e.g. 'invalid-tier:soft').
 */
export function describeInvalidWord(reason: string): string {
  if (reason === "empty-line" || reason === "empty-after-trim") return "blank line";
  if (reason === "duplicate-word") return "same word as an earlier line";
  if (reason === "empty-after-normalization") return "needs at least one letter or number";
  if (reason === "invalid-characters") return "has characters that won't draw well";
  const tier = /^invalid-tier:(.*)$/.exec(reason)?.[1];
  if (tier !== undefined) return `":${tier}" isn't a difficulty (try :easy, :medium, :hard)`;
  const len = /^too-long:(\d+)-graphemes$/.exec(reason)?.[1];
  if (len !== undefined) return `too long (${len} letters — ${MAX_GRAPHEMES} max)`;
  return reason;
}

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------

type PlayerListProps = {
  players: ReadonlyArray<SerializedPlayer>;
  hostId: string;
  myId: string | null;
  isHost: boolean;
  onKick: (playerId: string) => void;
};

function PlayerList({ players, hostId, myId, isHost, onKick }: PlayerListProps) {
  return (
    <div class="lobby-player-list" role="list" aria-label="Players in room">
      {players.map((player, idx) => (
        <div key={player.id} class="lobby-player" role="listitem">
          <span
            class="lobby-player-avatar"
            style={{ backgroundColor: player.color }}
            aria-hidden="true"
          />
          <span class="lobby-player-name">
            {player.name}
            {player.id === myId && " (you)"}
          </span>
          {player.id === hostId && (
            <span class="lobby-host-badge" aria-label="Room host">
              ✏
            </span>
          )}
          {isHost && player.id !== myId && player.id !== hostId && (
            <button
              class="lobby-kick-btn"
              onClick={() => onKick(player.id)}
              aria-label={`Kick ${player.name}`}
              type="button"
            >
              ✕
            </button>
          )}
          <span
            class={`lobby-player-underline ${SCRIBBLE_IN_CLASS}`}
            style={`--scribble-in-delay: ${idx * 40}ms`}
            data-testid="lobby-player-underline"
          >
            <ScribbleUnderline width={48} />
          </span>
        </div>
      ))}
      {players.length < MIN_PLAYERS_TO_START && (
        <p class="lobby-waiting-hint" aria-live="polite">
          Need {MIN_PLAYERS_TO_START - players.length} more player
          {MIN_PLAYERS_TO_START - players.length === 1 ? "" : "s"} to start
        </p>
      )}
    </div>
  );
}

type SettingsPanelProps = {
  settings: LobbySettings;
  isHost: boolean;
  onSettingsChange: (settings: Partial<LobbySettings>) => void;
  invalidWordLines: Array<{ lineIndex: number; original: string; reason: string }>;
};

function SettingsPanel({
  settings,
  isHost,
  onSettingsChange,
  invalidWordLines,
}: SettingsPanelProps) {
  return (
    <div class="lobby-settings-panel" aria-label="Game settings">
      <h3 class="lobby-settings-title">Settings</h3>

      <div class="lobby-setting-row">
        <ScribbleSelect
          label="Rounds"
          id="setting-rounds"
          value={String(settings.rounds)}
          onChange={(v) => onSettingsChange({ rounds: Number(v) })}
          options={ROUNDS_OPTIONS}
          disabled={!isHost}
        />
      </div>

      <div class="lobby-setting-row">
        <ScribbleSelect
          label="Draw time"
          id="setting-draw-timer"
          value={String(settings.drawTimerSeconds)}
          onChange={(v) => onSettingsChange({ drawTimerSeconds: Number(v) })}
          options={DRAW_TIMER_OPTIONS}
          disabled={!isHost}
        />
      </div>

      <div class="lobby-setting-row">
        <ScribbleSelect
          label="Word pick time"
          id="setting-word-pick-timer"
          value={String(settings.wordPickTimerSeconds)}
          onChange={(v) => onSettingsChange({ wordPickTimerSeconds: Number(v) })}
          options={WORD_PICK_TIMER_OPTIONS}
          disabled={!isHost}
        />
      </div>

      <div class="lobby-setting-row">
        <ScribbleToggle
          checked={settings.hintsEnabled}
          onChange={(v) => onSettingsChange({ hintsEnabled: v })}
          label="Hints"
          id="setting-hints"
          disabled={!isHost}
        />
      </div>

      <div class="lobby-setting-row">
        <SoundToggle />
      </div>

      <div class="lobby-setting-row">
        <ScribbleSelect
          label="Word list"
          id="setting-word-list"
          value={settings.useCustomWords ? "custom" : "default"}
          onChange={(v) => onSettingsChange({ useCustomWords: v === "custom" })}
          options={[
            { value: "default", label: "Default" },
            { value: "custom", label: "Custom" },
          ]}
          disabled={!isHost}
        />
      </div>

      {/* Custom word textarea: only shown when "Custom" selected */}
      {settings.useCustomWords && (
        <div class="lobby-custom-words">
          <label class="lobby-custom-words-label" for="custom-words-textarea">
            Custom words (one per line, optional :easy/:medium/:hard suffix)
          </label>
          <textarea
            id="custom-words-textarea"
            class="lobby-custom-words-textarea"
            rows={8}
            placeholder={"apple\nbanana:easy\nairplane:hard\n..."}
            value={settings.customWordsText}
            onInput={(e) =>
              onSettingsChange({ customWordsText: (e.target as HTMLTextAreaElement).value })
            }
            disabled={!isHost}
            aria-label="Custom word list"
          />
          {invalidWordLines.length > 0 && (
            <div class="lobby-custom-words-errors" role="alert">
              <p class="lobby-custom-words-error-title">
                {invalidWordLines.length} invalid line{invalidWordLines.length === 1 ? "" : "s"}:
              </p>
              {invalidWordLines.slice(0, 3).map((inv) => (
                <p key={inv.lineIndex} class="lobby-custom-words-error-line">
                  Line {inv.lineIndex + 1}: "{inv.original.slice(0, 30)}" —{" "}
                  {describeInvalidWord(inv.reason)}
                </p>
              ))}
            </div>
          )}
          {settings.customWordsText && (
            <p class="lobby-custom-words-count">
              {validateCustomWords(settings.customWordsText).validWords.length} valid words
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main LobbyShell
// ---------------------------------------------------------------------------

export function LobbyShell({
  roomId,
  players,
  playerId,
  hostId,
  sendFrame,
  authReject,
  serverSettings,
}: LobbyShellProps) {
  const isHost = playerId === hostId;
  const [settings, setSettings] = useState<LobbySettings>(DEFAULT_SETTINGS);

  // Task #15: host seeding. The HOST's local state is the editing source
  // of truth, but it must START from the room's real settings: a resumed
  // host (reload) and a promoted host (migration) used to see
  // DEFAULT_SETTINGS while the server held something else, and their next
  // single-field edit couldn't be sanity-checked against the rest. Seed
  // once when server settings first arrive, and re-seed on promotion.
  // The host's own edit echoes (SETTINGS_UPDATED → serverSettings prop)
  // must NOT clobber in-flight local edits, hence the one-shot gate.
  const wasHostRef = useRef(isHost);
  const seededRef = useRef(false);
  // True once the host has EDITED anything this session, vetoes the initial
  // seed so a same-tick "edit races the first effect flush" can't roll the
  // edit back while its echo is gate-blocked (review P3).
  const editedRef = useRef(false);
  useEffect(() => {
    const promoted = !wasHostRef.current && isHost;
    wasHostRef.current = isHost;
    if (promoted) {
      // Re-arm BEFORE the settings-availability check (review P3): a
      // re-promoted ex-host whose serverSettings briefly lag must still
      // get re-seeded when they arrive: the edge must not be consumed.
      seededRef.current = false;
      editedRef.current = false;
    }
    if (!isHost || !serverSettings) return;
    if (seededRef.current || editedRef.current) return;
    seededRef.current = true;
    setSettings(fromServerSettings(serverSettings));
  }, [isHost, serverSettings]);

  // Task #15: guest display. Guests render the server's canonical
  // settings verbatim (their panel is disabled anyway); falling back to
  // local defaults only while the first snapshot is in flight.
  const shownSettings = isHost
    ? settings
    : serverSettings
      ? fromServerSettings(serverSettings)
      : settings;
  const [startLoading, setStartLoading] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [kickedBanner, setKickedBanner] = useState<string | null>(null);
  const [invalidWordLines, setInvalidWordLines] = useState<
    Array<{ lineIndex: number; original: string; reason: string }>
  >([]);

  // Q9 latch plumbing: a ref mirror of startLoading so the reject effect and
  // the timeout callback read the CURRENT latch without re-arming on every
  // toggle, plus the pending fallback timer.
  const startLoadingRef = useRef(startLoading);
  startLoadingRef.current = startLoading;
  const startTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearStartTimeout = () => {
    if (startTimeoutRef.current !== null) {
      clearTimeout(startTimeoutRef.current);
      startTimeoutRef.current = null;
    }
  };
  useEffect(() => clearStartTimeout, []);

  // Q9 (audit 2026-06-11): the server refused START_GAME (phase race, host
  // migration mid-click). Pre-fix the button sat on "Starting…" forever,
  // observed live. Only a reject that lands while the latch is held counts;
  // a stale `lastAuthReject` surviving in ConnState from an earlier attempt
  // must not flash an error on remount.
  useEffect(() => {
    if (!authReject || !["not-authorized-start", "need-nine-distinct-custom-words", "start-game-failed"].includes(authReject.reason)) return;
    if (!startLoadingRef.current) return;
    clearStartTimeout();
    setStartLoading(false);
    setStartError(authReject.reason === "need-nine-distinct-custom-words"
      ? "Add at least 9 distinct valid custom words."
      : "The server said no to that start — try again.");
  }, [authReject]);

  // Validate custom words whenever the textarea changes
  useEffect(() => {
    if (!settings.useCustomWords || !settings.customWordsText) {
      setInvalidWordLines([]);
      return;
    }
    const { invalidLines } = validateCustomWords(settings.customWordsText);
    setInvalidWordLines(invalidLines);
  }, [settings.customWordsText, settings.useCustomWords]);

  const handleSettingsChange = (partial: Partial<LobbySettings>) => {
    if (!isHost) return;
    // Review P3: any edit vetoes the (not-yet-flushed) initial seed.
    editedRef.current = true;
    // Merge first so we can derive the wire payload from the NEXT settings,
    // not the stale closure: `customWords` is computed from the merged
    // `useCustomWords` + `customWordsText` below.
    const nextSettings = { ...settings, ...partial };
    setSettings(nextSettings);
    // M3.fix B3: dispatch UPDATE_SETTINGS over WS. The reducer-eligible fields
    // are a subset of LobbySettings (customWordsText is client-only render
    // state: the server stores the parsed `customWords` array instead).
    const wireSettings: {
      rounds?: number;
      drawTimerSeconds?: number;
      wordPickTimerSeconds?: number;
      hintsEnabled?: boolean;
      useCustomWords?: boolean;
      customWords?: ReadonlyArray<CustomWord>;
    } = {};
    if (partial.rounds !== undefined) wireSettings.rounds = partial.rounds;
    if (partial.drawTimerSeconds !== undefined)
      wireSettings.drawTimerSeconds = partial.drawTimerSeconds;
    if (partial.wordPickTimerSeconds !== undefined)
      wireSettings.wordPickTimerSeconds = partial.wordPickTimerSeconds;
    if (partial.hintsEnabled !== undefined) wireSettings.hintsEnabled = partial.hintsEnabled;
    if (partial.useCustomWords !== undefined) wireSettings.useCustomWords = partial.useCustomWords;
    // T16 fold-in: whenever the custom-words config changes (toggle flips or
    // the textarea is edited), send the validated word list so the server
    // actually stores it. When custom words are off, send an empty array so a
    // stale list never lingers in room.settings.
    if (partial.useCustomWords !== undefined || partial.customWordsText !== undefined) {
      wireSettings.customWords = nextSettings.useCustomWords
        ? validateCustomWords(nextSettings.customWordsText).validWords
        : [];
    }
    if (Object.keys(wireSettings).length > 0) {
      sendFrame?.({ kind: "UPDATE_SETTINGS", settings: wireSettings });
    }
  };

  const handleKick = (kickedPlayerId: string) => {
    if (!isHost) return;
    const kicked = players.find((p) => p.id === kickedPlayerId);
    if (kicked) {
      setKickedBanner(`${kicked.name} was kicked.`);
      setTimeout(() => setKickedBanner(null), 3000);
    }
    // M3.fix B3: dispatch KICK_PLAYER over WS. Server re-validates host.
    sendFrame?.({ kind: "KICK_PLAYER", playerId: kickedPlayerId });
  };

  // START_GAME button gating (§7 M3.T10):
  // - Need ≥MIN_PLAYERS_TO_START players (currently 2; was 3 pre-M5)
  // - If custom words: need ≥9 valid words
  const { validWords: customWordList } = settings.useCustomWords
    ? validateCustomWords(settings.customWordsText)
    : { validWords: [] };

  const canStart =
    players.length >= MIN_PLAYERS_TO_START &&
    (!settings.useCustomWords || customWordList.length >= 9) &&
    isHost &&
    !startLoading;

  const startDisabledReason = !isHost
    ? "Only the host can start"
    : players.length < MIN_PLAYERS_TO_START
      ? `Need ${MIN_PLAYERS_TO_START - players.length} more player${MIN_PLAYERS_TO_START - players.length === 1 ? "" : "s"}`
      : settings.useCustomWords && customWordList.length < 9
        ? `Need ${9 - customWordList.length} more distinct valid custom words`
        : null;

  const handleStartGame = () => {
    if (!canStart) return;
    setStartError(null);
    setStartLoading(true);
    // M3.fix B1: actually dispatch START_GAME over WS. The server's host-only
    // gate (server/src/index.ts) re-validates the actor and the ≥3 players
    // invariant. On success, the server broadcasts PHASE_CHANGED and the
    // client's WS handler transitions away from the lobby phase, naturally
    // unmounting this component: there's no separate "success" callback.
    const sent = sendFrame?.({ kind: "START_GAME" }) ?? false;
    if (!sent) {
      setStartLoading(false);
      setStartError("Not connected — please reload.");
      return;
    }
    // Leave startLoading=true so the button stays in "Starting…" until the
    // server's PHASE_CHANGED broadcast routes us away. Q9 backstop: if
    // neither PHASE_CHANGED nor AUTH_REJECT arrives, free the latch rather
    // than soft-lock the lobby.
    clearStartTimeout();
    startTimeoutRef.current = setTimeout(() => {
      startTimeoutRef.current = null;
      if (!startLoadingRef.current) return;
      setStartLoading(false);
      setStartError("No answer from the server — try again.");
    }, START_LATCH_TIMEOUT_MS);
  };

  return (
    <div
      class={`lobby-shell ${PAGE_TEAR_IN_CLASS}`}
      data-room-id={roomId}
      data-testid="lobby-shell"
    >
      {/* [1] Room name, 40% visual weight, top-left. T16 (4.3): the share CTA
          moved out of the header into the centered .lobby-share-band below. */}
      <header class="lobby-header">
        <div class="lobby-room-info">
          <h1 class="lobby-room-name">{getHostName(players, hostId)}'s Room</h1>
          <p class="lobby-room-id">Room code: {roomId}</p>
        </div>
      </header>

      {/* Empty-room nudge: surfaces the share CTA prominently while alone */}
      <EmptyRoomNudge roomId={roomId} playerCount={players.length} />

      {/* Kicked player banner */}
      {kickedBanner && <PaperBanner kind="info" message={kickedBanner} duration={3000} />}

      {/* Main lobby layout: player list (center) + settings (right rail) */}
      <div class="lobby-main">
        {/* [2] Player list: 25% visual weight, center */}
        <section class="lobby-players-section" aria-label="Players">
          {/* Audit 2026-06-11: the old "/10" advertised a cap nothing
              enforces (no MAX_PLAYERS exists anywhere). */}
          <h2 class="lobby-section-title">Players ({players.length})</h2>
          <PlayerList
            players={players}
            hostId={hostId}
            myId={playerId}
            isHost={isHost}
            onKick={handleKick}
          />
        </section>

        {/* [4] Settings panel: 15% visual weight, right rail */}
        <aside class="lobby-settings-aside">
          <SettingsPanel
            settings={shownSettings}
            isHost={isHost}
            onSettingsChange={handleSettingsChange}
            invalidWordLines={invalidWordLines}
          />
        </aside>
      </div>

      {/*
       * T16 (4.3): centered share band between the player/settings grid and the
       * Start button. Leads with the 4-char room CODE (rendered large+bold by
       * ShareLinkCTA), the full URL as a small caption.
       *
       * V4.1.T6-share (review P3): one share surface per state. While the owner
       * is alone the EmptyRoomNudge above is the sole share CTA; this band takes
       * over once a second player joins (room is live). Gating avoids two CTAs
       * duplicating the copy button + room code + aria-labels for assistive tech.
       */}
      {players.length >= 2 && (
        <div class="lobby-share-band" data-testid="lobby-share-band">
          <ShareLinkCTA roomId={roomId} />
        </div>
      )}

      {/* [3] Start game button: 20% visual weight, bottom-center, large.
          Audit 2026-06-11: guests used to get the SAME full-prominence button
          (merely disabled) plus two stacked explainer lines, an affordance
          that contradicted the permission. Guests now get one status line;
          only the host gets the button. */}
      <footer class="lobby-footer">
        {!isHost && (
          <p class="lobby-waiting-text" data-testid="lobby-guest-waiting" role="status">
            Waiting for {getHostName(players, hostId)} to start…
          </p>
        )}
        {isHost && (
          <ScribbleButton
            variant="primary"
            onClick={handleStartGame}
            disabled={!canStart}
            class="lobby-start-btn"
            aria-label={startDisabledReason ?? "Start the game"}
          >
            {startLoading ? "Starting…" : "Start game"}
          </ScribbleButton>
        )}
        {startDisabledReason && isHost && (
          <p class="lobby-disabled-reason" role="status">
            {startDisabledReason}
          </p>
        )}
        {startError && (
          <p class="lobby-disabled-reason" role="alert">
            {startError}
          </p>
        )}
      </footer>
    </div>
  );
}
